import { Typography, Box, Theme, useTheme } from '@mui/material';
import {
  DataGridPremium,
  GridApiPremium,
  GridColDef,
  GridListViewColDef,
  GridValidRowModel,
  GridRenderCellParams,
  GridInitialState,
  GridTreeDataGroupingCell,
  type GridGroupNode,
} from '@mui/x-data-grid-premium';
import { defaultToolbarExportSlotProps } from '@/components/table/toolbarSlotProps';
import { createExcelExportWorker } from '@/utils/excelExportWorker';
import {
  aggregateHistoricalComparisons,
  formatHistoricalCurrency,
  formatHistoricalPercentFixed,
  lookupLiveEndingBalance,
  withHistoricalChangeForRange,
  withLiveEndingBalance,
} from '@/utils/historicalBalance';
import { useGetAccountHistoricalComparisonQuery } from '@/store/accountsApi';
import {
  historicalComparisonQueryArg,
  isCustomGroupBy,
} from './historicalComparisonQueryArg';
import { skipToken } from '@reduxjs/toolkit/query';
import { useMemo } from 'react';
import { APP_DATE_DISPLAY_FORMAT } from '@/constants/dateFormats';
import { formatAppDate } from '@/utils/date';
import { filterByInScopeIds } from '@/utils/historicalAccountScope';
import { DataRangeFilterEnum } from '@/constants/dates';
import { useLocalization } from '@/hooks/useLocalization';
import { useAccountCreatedDates } from '@/hooks/useAccountCreatedDates';
import { useInScopeAccountIds } from '@/hooks/useInScopeAccountIds';
import { mobileWidth } from '@/constants';
import { GRID_INITIAL_PAGINATION } from '@/constants/pagination';
import { useMediaQuery } from '@mui/material';
import { AccountHistoricalComparisonItem } from '@/models/AccountViewModel';
import { useAuthIsLoaded } from '@/hooks/useAuthIsLoaded';
import CustomToolbarForNative from '@/components/CustomToolbarForNative';
import XsPreferencesDrawerToolbar from '@/components/table/XsPreferencesDrawerToolbar';
import { Capacitor } from '@capacitor/core';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useWidgetWidthIsBelow } from '@/hooks/useWidgetWidthIsBelow';
import { GRID_COLUMN_WIDTH_NARROW } from '@/constants/dimensions';
import {
  GridContainer,
  type GridContainerSize,
} from '@/components/grids/GridContainer';

interface GridRow extends GridValidRowModel {
  id: string;
  Name: string;
  groupName: string;
  groupSort: string | number;
  isGroupTotal?: boolean;
  sortIndex: number;
  isAsset: boolean;
  classificationSort: number;
  OldDollarBalance: number | null;
  DollarBalance: number | null;
  DollarChange: number | null;
  PercentChange: number | null;
  AccountIcon?: string;
}

interface TreeGridRow extends GridRow {
  parentId?: string | null;
  path?: string[];
  isGrandTotal?: boolean;
}

interface GrandTotal {
  OldDollarBalance: number | null;
  DollarBalance: number | null;
  DollarChange: number | null;
  PercentChange: number | null;
}

type BalanceField = 'OldDollarBalance' | 'DollarBalance' | 'DollarChange';

interface GroupTotals {
  OldDollarBalance: number | null;
  DollarBalance: number | null;
  DollarChange: number | null;
  PercentChange: number | null;
  count: number;
}

type GroupTotalsByName = Map<string, GroupTotals>;

type GroupNodeWithCounts = GridGroupNode & {
  descendantCount?: number;
  childrenIds?: Array<string | number>;
};

interface AccountsHistoricalGridProps {
  dateFilter: DataRangeFilterEnum;
  startDate?: string;
  endDate?: string;
  accountId?: number;
  /** Accounts-page balance for `accountId` when the accounts list omits it. */
  liveBalance?: number;
  isWidget?: boolean;
  size?: GridContainerSize;
  groupBy?: string;
  excludedAccounts?: number[];
  excludedAccountTypes?: number[];
  excludedCategories?: number[];
  excludedInstitutions?: number[];
}

const INITIAL_GRID_STATE_GROUPED: GridInitialState = {
  sorting: {
    sortModel: [
      { field: 'groupName', sort: 'asc' },
      { field: 'sortIndex', sort: 'asc' },
    ],
  },
  aggregation: {
    model: {
      OldDollarBalance: 'sum',
      DollarBalance: 'sum',
      DollarChange: 'sum',
    },
  },
  pagination: GRID_INITIAL_PAGINATION,
};

const gridStyles = {
  '& .MuiDataGrid-cell': {
    display: 'flex',
    flexWrap: 'nowrap',
    width: '100%',
    minWidth: '100%',
    maxWidth: '100%',
  },
};

const groupKeyLabel = (key: GridGroupNode['groupingKey']): string => {
  if (key == null) return '';
  return typeof key === 'string' ? key : String(key);
};

const groupDescendantCount = (node: GridGroupNode): number => {
  const withCounts = node as GroupNodeWithCounts;
  return (
    withCounts.descendantCount ??
    withCounts.childrenIds?.length ??
    node.children.length
  );
};

const getGroupTotals = (
  totalsByName: GroupTotalsByName,
  groupName: string,
): GroupTotals | undefined => totalsByName.get(groupName);

const toDisplayString = (
  value: string | number | boolean | null | undefined,
): string => {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return '';
};

const getAccountIcon = (data: GridRow) => {
  if (data.AccountIcon) {
    return <span className={`fa ${data.AccountIcon}`} />;
  }
  return null;
};

const createBaseColumnProps = () => ({
  headerAlign: 'right' as const,
  align: 'right' as const,
});

// Note: hooks (like useMediaQuery) must be called inside a React component.

const createNameColumn = (
  t: (key: string) => string,
): GridColDef<TreeGridRow> => ({
  field: 'Name',
  headerName: t('name'),
  flex: 1,
  minWidth: 300,
  renderCell: (params: GridRenderCellParams<TreeGridRow>) => {
    try {
      const { row, rowNode } = params;
      const isTotal = row.isGroupTotal;
      const isGrandTotal = row.isGrandTotal;

      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            height: '100%',
            pl: rowNode.depth > 0 ? 2 : 0,
          }}
        >
          <Typography
            variant='body2'
            sx={{
              fontWeight: isTotal || isGrandTotal ? 600 : 400,
              color: 'text.primary',
              flexGrow: 1,
            }}
          >
            {row.Name}
          </Typography>
          {!isTotal && getAccountIcon(row)}
        </Box>
      );
    } catch (error) {
      console.error('Error in Name renderCell:', error);
      return null;
    }
  },
});

const getValueColor = (value: number | null | undefined) => {
  if (value && value > 0) return 'success.main';
  if (value && value < 0) return 'error.main';
  return 'text.secondary';
};

const createBalanceColumn = (
  field: BalanceField,
  headerName: string,
  totalsByName: GroupTotalsByName,
  emptyLabel: string,
): GridColDef<TreeGridRow> => ({
  ...createBaseColumnProps(),
  field,
  headerName,
  width: GRID_COLUMN_WIDTH_NARROW,
  renderCell: (params: GridRenderCellParams<TreeGridRow, number>) => {
    if (params.rowNode.type === 'group') {
      const groupName = groupKeyLabel(params.rowNode.groupingKey);
      const value = getGroupTotals(totalsByName, groupName)?.[field];

      if (value !== undefined) {
        return (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
            }}
          >
            <Typography
              variant='body2'
              sx={{
                color: getValueColor(value),
                fontWeight: 'bold',
              }}
            >
              {formatHistoricalCurrency(value, emptyLabel)}
            </Typography>
          </Box>
        );
      }
      return null;
    }
    const value = params.value;
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
        }}
      >
        <Typography
          variant='body2'
          sx={{ color: getValueColor(value) }}
        >
          {formatHistoricalCurrency(value, emptyLabel)}
        </Typography>
      </Box>
    );
  },
});

const createPercentageColumn = (
  t: (key: string, defaultVal: string) => string,
  totalsByName: GroupTotalsByName,
  emptyLabel: string,
): GridColDef<TreeGridRow> => ({
  ...createBaseColumnProps(),
  field: 'PercentChange',
  headerName: t('grid.percentChange', '% Change'),
  width: GRID_COLUMN_WIDTH_NARROW,
  renderCell: (params: GridRenderCellParams<TreeGridRow, number>) => {
    if (params.rowNode.type === 'group') {
      const groupName = groupKeyLabel(params.rowNode.groupingKey);
      const value = getGroupTotals(totalsByName, groupName)?.PercentChange;

      if (value !== undefined) {
        return (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
            }}
          >
            <Typography
              variant='body2'
              sx={{
                color: getValueColor(value),
                fontWeight: 'bold',
              }}
            >
              {formatHistoricalPercentFixed(value, emptyLabel)}
            </Typography>
          </Box>
        );
      }
      return null;
    }

    const value = params.value;

    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
        }}
      >
        <Typography
          variant='body2'
          sx={{ color: getValueColor(value) }}
        >
          {formatHistoricalPercentFixed(value, emptyLabel)}
        </Typography>
      </Box>
    );
  },
});

const CustomFooter = ({
  grandTotal,
  emptyLabel,
}: {
  grandTotal: GrandTotal | null;
  emptyLabel: string;
}) => (
  <Box
    sx={{
      borderTop: (theme: Theme) => `2px solid ${theme.palette.divider}`,
      display: 'grid',
      gridTemplateColumns: 'minmax(300px, 1fr) repeat(4, 150px)',
      alignItems: 'center',
      px: 2,
      py: 2,
    }}
  >
    <Typography
      variant='body2'
      sx={{ fontWeight: 600, pl: 1 }}
    >
      Total:
    </Typography>
    <Typography
      variant='body2'
      sx={{
        fontWeight: 600,
        textAlign: 'right',
        pr: 2,
        color: getValueColor(grandTotal?.OldDollarBalance),
      }}
    >
      {formatHistoricalCurrency(grandTotal?.OldDollarBalance, emptyLabel)}
    </Typography>
    <Typography
      variant='body2'
      sx={{
        fontWeight: 600,
        textAlign: 'right',
        pr: 2,
        color: getValueColor(grandTotal?.DollarBalance),
      }}
    >
      {formatHistoricalCurrency(grandTotal?.DollarBalance, emptyLabel)}
    </Typography>
    <Typography
      variant='body2'
      sx={{
        fontWeight: 600,
        textAlign: 'right',
        pr: 2,
        color: getValueColor(grandTotal?.DollarChange),
      }}
    >
      {formatHistoricalCurrency(grandTotal?.DollarChange, emptyLabel)}
    </Typography>
    <Typography
      variant='body2'
      sx={{
        fontWeight: 600,
        textAlign: 'right',
        pr: 2,
        color: getValueColor(grandTotal?.PercentChange),
      }}
    >
      {formatHistoricalPercentFixed(grandTotal?.PercentChange, emptyLabel)}
    </Typography>
  </Box>
);

const AccountsHistoricalGrid = ({
  startDate,
  endDate,
  accountId,
  liveBalance,
  isWidget = false,
  size,
  groupBy = 'none',
  excludedAccounts = [],
  excludedAccountTypes = [],
  excludedCategories = [],
  excludedInstitutions = [],
}: AccountsHistoricalGridProps) => {
  const { t } = useLocalization();
  const emptyLabel = t('common.emdash', '—');
  const authIsLoaded = useAuthIsLoaded();
  const isMobileMatch = useMediaQuery(mobileWidth);
  const theme = useTheme();
  const { ref: widgetWidthRef, isBelow: isWidgetBelowXsWidth } =
    useWidgetWidthIsBelow(isWidget, theme.breakpoints.values.sm);
  const shouldUseListView = isMobileMatch || (isWidget && isWidgetBelowXsWidth);
  const isSingleAccountView = !!accountId;
  const isCustomGroup = isCustomGroupBy(groupBy);
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- guard Capacitor.getPlatform being absent on non-Capacitor runtimes (typed non-nullable but can be undefined when the shim isn't initialized)
  const isNative = Capacitor.getPlatform && Capacitor.getPlatform() !== 'web';
  const {
    accountIds: inScopeAccountIds,
    balancesByAccountId,
    isLoading: isScopeLoading,
  } = useInScopeAccountIds({
    endDate,
    skipScope: isSingleAccountView,
    excludedAccounts,
    excludedAccountTypes,
    excludedCategories,
    excludedInstitutions,
  });
  const {
    createdDates,
    isLoading: areCreatedDatesLoading,
    isError: createdDatesFailed,
  } = useAccountCreatedDates(endDate);

  const { data, isLoading, isFetching, error } =
    useGetAccountHistoricalComparisonQuery(
      authIsLoaded && startDate && endDate
        ? historicalComparisonQueryArg({
            startDate,
            endDate,
            groupBy,
            accountId,
            excludedAccounts,
            excludedAccountTypes,
            excludedCategories,
            excludedInstitutions,
          })
        : skipToken,
      {
        skip: !authIsLoaded || !startDate || !endDate,
        refetchOnMountOrArgChange: true,
      },
    );

  const { rows, groupTotalsByName } = useMemo(() => {
    const emptyTotals: GroupTotalsByName = new Map();
    const sourceRecords =
      areCreatedDatesLoading || createdDatesFailed ? [] : (data?.Records ?? []);
    const records = isSingleAccountView
      ? sourceRecords
      : filterByInScopeIds(
          sourceRecords,
          (record) => record.Id,
          inScopeAccountIds,
        );

    const activeRecords = records
      .filter((record) => !record.ClosedDate)
      .map((record) =>
        withHistoricalChangeForRange(
          withLiveEndingBalance(
            record,
            lookupLiveEndingBalance(
              record.Id,
              balancesByAccountId,
              accountId,
              liveBalance,
            ),
            endDate,
          ),
          {
            createdDate: createdDates.get(record.Id),
            startDate,
            endDate,
          },
        ),
      );

    if (isSingleAccountView) {
      return {
        rows: activeRecords.map((record) => ({
          ...record,
          id: record.Id.toString(),
          groupName: '',
          groupSort: 0,
          isAsset: record.IsAsset,
          classificationSort: 0,
          sortIndex: 0,
        })),
        groupTotalsByName: emptyTotals,
      };
    }

    const getGroupName = (record: AccountHistoricalComparisonItem): string => {
      if (isCustomGroup) {
        return record.GroupName || 'Not Set';
      }
      switch (groupBy) {
        case 'institution':
          return record.InstitutionName || 'No Institution';
        case 'data source':
          // Data for this grouping is not available in this component's API response.
          // Falling back to classification grouping.
          return record.GroupName || 'Unclassified';
        case 'none':
        default:
          return record.GroupName || 'Unclassified';
      }
    };

    const groupedRecords = new Map<string, AccountHistoricalComparisonItem[]>();
    for (const record of activeRecords) {
      const groupName = getGroupName(record);
      const bucket = groupedRecords.get(groupName);
      if (bucket) {
        bucket.push(record);
      } else {
        groupedRecords.set(groupName, [record]);
      }
    }

    const allRows: TreeGridRow[] = [];
    const nextTotals: GroupTotalsByName = new Map();
    const sortedGroupKeys = [...groupedRecords.keys()].sort((a, b) =>
      a.localeCompare(b),
    );

    for (const groupName of sortedGroupKeys) {
      const groupRecords = groupedRecords.get(groupName);
      if (!groupRecords) continue;

      groupRecords.forEach((record, index) => {
        allRows.push({
          ...record,
          id: record.Id.toString(),
          groupName,
          groupSort: index,
          isAsset: record.IsAsset,
          classificationSort: index,
          sortIndex: index,
          isGroupTotal: false,
          OldDollarBalance: record.OldDollarBalance,
          DollarBalance: record.DollarBalance,
          DollarChange: record.DollarChange,
          PercentChange: record.PercentChange,
          path: [groupName, `${record.Name}_${String(record.Id)}`],
        });
      });

      nextTotals.set(groupName, {
        ...aggregateHistoricalComparisons(groupRecords),
        count: groupRecords.length,
      });
    }

    return { rows: allRows, groupTotalsByName: nextTotals };
  }, [
    areCreatedDatesLoading,
    createdDates,
    createdDatesFailed,
    data,
    accountId,
    balancesByAccountId,
    endDate,
    inScopeAccountIds,
    isSingleAccountView,
    liveBalance,
    groupBy,
    isCustomGroup,
    startDate,
  ]);

  const grandTotal = useMemo(() => {
    if (!rows.length || isSingleAccountView) return null;

    return aggregateHistoricalComparisons(rows);
  }, [rows, isSingleAccountView]);

  const columns: GridColDef<TreeGridRow>[] = useMemo(
    () => [
      createNameColumn(t),
      createBalanceColumn(
        'OldDollarBalance',
        startDate
          ? `${formatAppDate(startDate, { format: APP_DATE_DISPLAY_FORMAT })} ${t(
              'grid.balance',
              'Value',
            )}`
          : '',
        groupTotalsByName,
        emptyLabel,
      ),
      createBalanceColumn(
        'DollarBalance',
        endDate
          ? `${formatAppDate(endDate, { format: APP_DATE_DISPLAY_FORMAT })} ${t(
              'grid.balance',
              'Value',
            )}`
          : '',
        groupTotalsByName,
        emptyLabel,
      ),
      createBalanceColumn(
        'DollarChange',
        t('grid.dollarChange', '$ Change'),
        groupTotalsByName,
        emptyLabel,
      ),
      createPercentageColumn(t, groupTotalsByName, emptyLabel),
    ],
    [emptyLabel, startDate, endDate, t, groupTotalsByName],
  );

  const listViewColDef: GridListViewColDef<TreeGridRow> = useMemo(
    () => ({
      field: 'listColumn',
      renderCell: (
        params: GridRenderCellParams<
          TreeGridRow,
          string | number | boolean | null
        >,
      ) => {
        const { row, rowNode, api } = params;
        const visibilityModel = api.state.columns.columnVisibilityModel;
        const isFieldVisible = (field: string) =>
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-boolean-literal-compare -- visible columns are omitted from the model; only an explicit false hides them
          visibilityModel[field] !== false;

        const startDateLabel = startDate
          ? formatAppDate(startDate, { format: APP_DATE_DISPLAY_FORMAT })
          : '';
        const endDateLabel = endDate
          ? formatAppDate(endDate, { format: APP_DATE_DISPLAY_FORMAT })
          : '';

        // Group header row (tree data grouping)
        if (!isSingleAccountView && rowNode.type === 'group') {
          const groupKey =
            groupKeyLabel(rowNode.groupingKey) || toDisplayString(params.value);
          const groupTotals = getGroupTotals(groupTotalsByName, groupKey);
          const count = groupTotals?.count ?? groupDescendantCount(rowNode);
          const isExpanded = !!rowNode.childrenExpanded;

          const handleToggleExpand = (event: React.MouseEvent) => {
            event.stopPropagation();
            event.preventDefault();
            (params.api as GridApiPremium).setRowChildrenExpansion(
              params.id,
              !isExpanded,
            );
          };

          return (
            <Box sx={{ width: '100%', py: 1.5 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  width: '100%',
                  px: 0,
                }}
              >
                <Box
                  onClick={handleToggleExpand}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 28,
                    height: 28,
                    flex: '0 0 0px',
                    cursor: 'pointer',
                  }}
                >
                  {isExpanded ? (
                    <ExpandMoreIcon fontSize='small' />
                  ) : (
                    <ChevronRightIcon fontSize='small' />
                  )}
                </Box>

                <Typography
                  variant='body2'
                  color='text.primary'
                  sx={{ fontWeight: '600 !important', flex: 1, minWidth: 0 }}
                >
                  {count > 0 ? `${groupKey} (${String(count)})` : groupKey}
                </Typography>

                <Typography
                  variant='body2'
                  color='text.primary'
                  sx={{
                    fontWeight: '600 !important',
                    textAlign: 'right',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {groupTotals
                    ? formatHistoricalCurrency(
                        typeof groupTotals.DollarBalance === 'number'
                          ? groupTotals.DollarBalance
                          : null,
                        emptyLabel,
                      )
                    : ''}
                </Typography>
              </Box>
            </Box>
          );
        }

        const Line = ({
          left,
          right,
          rightColor = 'text.secondary',
        }: {
          left: string;
          right: string;
          rightColor?: string;
        }) => (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              gap: 1,
              mt: 0.5,
              justifyContent: 'space-between',
            }}
          >
            <Typography
              variant='body2'
              color='text.secondary'
              sx={{
                fontWeight: 400,
                flex: '1 1 auto',
                minWidth: 0,
                pr: 1,
              }}
            >
              {left}
            </Typography>
            <Typography
              variant='body2'
              sx={{
                fontWeight: 400,
                textAlign: 'right',
                whiteSpace: 'nowrap',
                color: rightColor,
                flex: '0 0 auto',
                ml: 'auto',
              }}
            >
              {right}
            </Typography>
          </Box>
        );

        const showName = isFieldVisible('Name');
        const showStart = isFieldVisible('OldDollarBalance');
        const showEnd = isFieldVisible('DollarBalance');
        const showDollarChange = isFieldVisible('DollarChange');
        const showPercentChange = isFieldVisible('PercentChange');

        const dollarChange = row.DollarChange;
        const percentChange = row.PercentChange;

        return (
          <Box sx={{ width: '100%', py: 2 }}>
            {showName ? (
              <Typography
                variant='body2'
                color='text.primary'
                sx={{ fontWeight: '600 !important' }}
              >
                {row.Name}
              </Typography>
            ) : null}

            {showStart ? (
              <Line
                left={startDateLabel}
                right={formatHistoricalCurrency(
                  row.OldDollarBalance,
                  emptyLabel,
                )}
              />
            ) : null}

            {showEnd ? (
              <Line
                left={endDateLabel}
                right={formatHistoricalCurrency(row.DollarBalance, emptyLabel)}
              />
            ) : null}

            {showDollarChange ? (
              <Line
                left={t('grid.dollarChange', '$ Change')}
                right={formatHistoricalCurrency(dollarChange, emptyLabel)}
                rightColor={getValueColor(dollarChange)}
              />
            ) : null}

            {showPercentChange ? (
              <Line
                left={t('grid.percentChange', '% Change')}
                right={formatHistoricalPercentFixed(percentChange, emptyLabel)}
                rightColor={getValueColor(percentChange)}
              />
            ) : null}
          </Box>
        );
      },
    }),
    [emptyLabel, endDate, groupTotalsByName, isSingleAccountView, startDate, t],
  );

  if (error || createdDatesFailed) {
    return (
      <Typography
        color='error'
        sx={{ p: 3 }}
      >
        {t('errorLoadingBalanceSheetData', 'Error loading balance sheet data')}
      </Typography>
    );
  }

  const CustomGroupingCell = (
    params: GridRenderCellParams<TreeGridRow, string | number | boolean | null>,
  ) => {
    if (params.row.isGroupTotal || params.rowNode.type !== 'group') {
      return null;
    }

    const groupName =
      groupKeyLabel(params.rowNode.groupingKey) ||
      toDisplayString(params.value);
    const groupTotals = getGroupTotals(groupTotalsByName, groupName);
    const groupingParams = {
      ...params,
      rowNode: groupTotals
        ? { ...params.rowNode, descendantCount: groupTotals.count }
        : params.rowNode,
    };

    return <GridTreeDataGroupingCell {...groupingParams} />;
  };

  return (
    <Box
      ref={widgetWidthRef}
      sx={{ px: 0, mt: 3, width: '100%' }}
    >
      <GridContainer
        isWidget={isWidget}
        size={size}
      >
        <DataGridPremium
          rows={rows}
          columns={columns}
          listView={shouldUseListView}
          {...(shouldUseListView ? { listViewColumn: listViewColDef } : {})}
          {...(shouldUseListView
            ? { getRowHeight: () => 'auto' as const }
            : {})}
          treeData={!isSingleAccountView}
          defaultGroupingExpansionDepth={-1}
          getTreeDataPath={
            !isSingleAccountView
              ? (row: TreeGridRow) => row.path || []
              : undefined
          }
          groupingColDef={
            !isSingleAccountView
              ? {
                  hideDescendantCount: false,
                  renderCell: CustomGroupingCell,
                }
              : undefined
          }
          showToolbar
          disablePivoting
          initialState={!isSingleAccountView ? INITIAL_GRID_STATE_GROUPED : {}}
          slots={{
            columnMenu: () => null,
            footer: () =>
              !isSingleAccountView ? (
                <CustomFooter
                  grandTotal={grandTotal}
                  emptyLabel={emptyLabel}
                />
              ) : null,
            ...(isNative
              ? {
                  toolbar: (props) => (
                    <CustomToolbarForNative
                      {...props}
                      excelWorker={createExcelExportWorker}
                    />
                  ),
                }
              : shouldUseListView
                ? { toolbar: XsPreferencesDrawerToolbar }
                : {}),
          }}
          slotProps={{
            loadingOverlay: {
              variant: 'linear-progress',
              noRowsVariant: 'skeleton',
            },
            // Prevent users from hiding internal grouping columns (tree data) in the Columns drawer.
            // This keeps "Groups" always visible when grouping is enabled.
            columnsManagement: {
              getTogglableColumns: (cols: GridColDef[]) =>
                cols
                  .filter((c) => c.hideable !== false)
                  .map((c) => c.field)
                  .filter(
                    (field) =>
                      field !== 'actions' &&
                      field !== 'listColumn' &&
                      field !== '__list__' &&
                      !field.startsWith('__'),
                  ),
            },
            columnsPanel: {
              getTogglableColumns: (cols: GridColDef[]) =>
                cols
                  .filter((c) => c.hideable !== false)
                  .map((c) => ({ field: c.field }))
                  .filter(
                    (c) =>
                      c.field !== 'actions' &&
                      c.field !== 'listColumn' &&
                      c.field !== '__list__' &&
                      !c.field.startsWith('__'),
                  ),
            },
            ...(isMobileMatch
              ? { toolbar: { showQuickFilter: true } }
              : {
                  toolbar: {
                    ...defaultToolbarExportSlotProps,
                    showQuickFilter: true,
                    excelOptions: {
                      ...defaultToolbarExportSlotProps.excelOptions,
                      worker: createExcelExportWorker,
                    },
                  },
                }),
          }}
          disableColumnMenu
          disableRowSelectionOnClick
          loading={
            isLoading || isFetching || isScopeLoading || areCreatedDatesLoading
          }
          sx={
            isMobileMatch
              ? {
                  ...gridStyles,
                  width: '100%',
                  maxWidth: '100%',
                  minWidth: 0,
                  overflowX: 'hidden',
                  // In list view we don't need column headers; hiding them also prevents header-width overflow.
                  '& .MuiDataGrid-columnHeaders': {
                    display: 'none',
                  },
                  '& .MuiDataGrid-toolbarContainer': {
                    width: '100%',
                    maxWidth: '100%',
                  },
                  // In list view, force the single list column to actually fill the grid width.
                  '& .MuiDataGrid-main': {
                    width: '100%',
                    maxWidth: '100%',
                    overflowX: 'hidden',
                  },
                  '& .MuiDataGrid-virtualScroller': {
                    width: '100%',
                    maxWidth: '100%',
                    overflowX: 'hidden',
                  },
                  // MUI often sizes this to the total columns width; in list view we want 100%.
                  '& .MuiDataGrid-virtualScrollerRenderZone': {
                    width: '100% !important',
                    minWidth: '100% !important',
                    maxWidth: '100% !important',
                  },
                  '& .MuiDataGrid-virtualScrollerContent': {
                    width: '100% !important',
                    minWidth: '100% !important',
                    maxWidth: '100% !important',
                  },
                  '& .MuiDataGrid-virtualScrollerContent > div': {
                    width: '100% !important',
                    minWidth: '100% !important',
                    maxWidth: '100% !important',
                  },
                  '& .MuiDataGrid-row': {
                    display: 'flex',
                    flexWrap: 'nowrap',
                    width: '100% !important',
                    minWidth: '100% !important',
                    maxWidth: '100% !important',
                  },
                  '& .MuiDataGrid-cell': {
                    display: 'flex',
                    flexWrap: 'nowrap',
                    width: '100%',
                    minWidth: '100%',
                    maxWidth: '100%',
                    flex: '1 1 0',
                    px: 3,
                  },
                  '& .MuiDataGrid-cellContent': {
                    width: '100%',
                    flex: 1,
                  },
                }
              : gridStyles
          }
        />
      </GridContainer>
    </Box>
  );
};

export default AccountsHistoricalGrid;
